import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";

const COMPLIANCE_LOGOS = [
  {
    name: "CCPA",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/CCPA-Compliant.svg",
  },
  {
    name: "GDPR",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/GDPR-Compliant.svg",
  },
  {
    name: "HIPAA",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/HIPAA-Compliant.svg",
  },
  {
    name: "HITRUST",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/HITRUST.svg",
  },
  {
    name: "ISO 27001",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/ISO-27001.svg",
  },
  {
    name: "PCI DSS",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/PCI-DSS-Compliant.svg",
  },
  {
    name: "SOC 2",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/SOC-2.svg",
  },
  {
    name: "FedRAMP",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/FR-FedRamp.svg",
  },
  {
    name: "NIST",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/NIST.svg",
  },
  {
    name: "DORA",
    url: "https://rhymetec.com/wp-content/uploads/2025/10/Dora.svg",
  },
];

export default function GRC() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Governance, Risk & Compliance (GRC) Services | Synthera Security
        </title>

        <meta
          name="description"
          content="Governance, Risk and Compliance (GRC) consulting services including ISO 27001, NIST CSF, SOC 2, risk assessments, compliance programs, and cybersecurity governance."
        />

        <meta
          name="keywords"
          content="GRC Services, Governance Risk Compliance, ISO 27001, NIST CSF, SOC 2, Risk Assessment, Compliance Consulting, Cybersecurity Governance, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Governance, Risk & Compliance (GRC) Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="Strengthen cybersecurity governance, manage risk, and achieve compliance with expert GRC consulting services."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/grc"
        />

        <link rel="canonical" href="https://www.syntherasecurity.com/grc" />
      </Helmet>

      <section className="bg-[#032d28] text-white relative overflow-hidden flex flex-col items-center py-24 min-h-screen">
        {/* BACK BUTTON */}
        <Link
          to="/services/security-consulting-advisory"
          className="absolute top-20 left-8 text-white/40 hover:text-[#24ff81] transition-colors"
        >
          <ArrowLeft size={26} />
        </Link>

        {/* TEXT CONTENT CONTAINER */}
        <div className="max-w-3xl mx-auto text-left w-full px-6">
          {/* IMAGE */}
          <div className="flex justify-center mb-10">
            <img
              src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1776681892/GRC_drphtp.jpg"
              alt="GRC"
              className="w-44 h-44 rounded-full object-cover shadow-xl"
            />
          </div>

          {/* TITLE */}
          <h1 className="text-3xl md:text-4xl font-bold tracking-wide mb-6 uppercase text-center">
            Governance, Risk and Compliance
          </h1>

          {/* INTRO */}
          <p className="text-white/80 leading-relaxed mb-10">
            <b>Governance, Risk and Compliance (GRC)</b> is about ensuring your
            organisation is hitting its cyber security targets as defined in
            regulation or policy, with clear accountabilities and pragmatic
            management of risk.
          </p>

          {/* BENEFITS */}
          <h2 className="text-xl font-bold mb-4">
            The benefits of effective GRC are:
          </h2>

          <div className="space-y-4 text-white/80 mb-12">
            <p>
              <b>Clearer, more actionable</b> security programs supported by the
              definition, documentation, and communication of effective
              governance structures and responsibilities.
            </p>

            <p>
              <b>Increased Return on Investment (ROI)</b> from the cybersecurity
              budget by defining and prioritising control selection and
              implementation through pragmatic cyber security risk assessment.
            </p>

            <p>
              <b>Picking the right tool for the right job</b> by understanding
              your organisation’s underlying business drivers and external
              dependencies and translating this into the selection and
              implementation of leading security standards.
            </p>
          </div>

          {/* APPROACH */}
          <h2 className="text-xl font-bold mb-4">Our Approach</h2>
          <p className="text-white/80 leading-relaxed mb-12">
            We believe choosing the right security standard for the right
            objective is critical to success. Whether you're defining top-level
            management intent through your ISO27001-aligned ISMS, improving
            control selection via the NIST-CSF, or building a System Security
            Plan for a PROTECTED system, we're here to help.
          </p>

          {/* EXPERIENCE */}
          <h2 className="text-xl font-bold mb-4">Our Experience</h2>
          <p className="text-white/80 leading-relaxed mb-12">
            We have experience helping organisations around the world define and
            achieve their compliance goals and manage cyber risk. We appreciate
            that not all compliance programs are created equal. Working with
            your key stakeholders to ensure you are achieving compliance or risk
            objectives with minimal impact to your core business is our passion
            seriously.
          </p>

          {/* KEY SERVICES */}
          <h2 className="text-xl font-bold mb-4">Key Services</h2>
          <p className="text-white/80 mb-4">
            Our most utilised GRC services are:
          </p>

          <ul className="list-disc pl-6 space-y-3 text-white/80 mb-14">
            <li>
              <b>Threat and Risk Assessments</b>, ISO31000:2018-aligned cyber
              security risk assessments in which threat modelling is built into
              determining cyber risk exposure for more informed decision-making.
            </li>

            <li>
              <b>ISO27001 Compliance</b>, including audit by certified ISO27001
              Lead Auditors and Information Security Management System (ISMS)
              documentation development.
            </li>

            <li>
              <b>IRAP Assessments</b>, by ASD endorsed cyber security
              professionals to provide assurance of the security of systems
              storing or processing Australian Government data.
            </li>

            <li>
              <b>
                Information Security Manual (ISM) or Essential Eight assessments
              </b>
              , for organisations responsible for the storage or processing of
              Australian Government information.
            </li>

            <li>
              <b>SOC 2 Reporting</b>, in which Trust Services Principles are
              used to assess and report on the confidentiality, availability
              integrity, of a service organisation’s systems and processes.
            </li>

            <li>
              <b>NIST-CSF Maturity Assessments</b>, benchmarking cyber security
              capabilities according to Identify, Protect, Detect, Respond,
              Recover.
            </li>

            <li>
              <b>State Government Attestations</b>, such as Mandatory 25
              assessments in New South Wales, VPDSS reporting in Victoria or
              meeting IS18 requirements in Queensland.
            </li>
          </ul>
        </div>

        {/* FULL-WIDTH CERTIFICATION SLIDER (Touches side corners) */}
        <div className="w-full bg-white py-16 mb-14 overflow-hidden">
          <h3 className="text-[#032d28] text-center font-bold mb-10 uppercase tracking-widest text-sm px-6">
            Compliance Standards & Certifications
          </h3>
          <div className="relative">
            <motion.div
              className="flex gap-16 items-center"
              animate={{ x: [0, -1800] }}
              transition={{ repeat: Infinity, duration: 40, ease: "linear" }}
            >
              {[...COMPLIANCE_LOGOS, ...COMPLIANCE_LOGOS].map((cert, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center min-w-[120px]"
                >
                  <img
                    src={cert.url}
                    alt={cert.name}
                    className="w-20 h-20 object-contain mb-4 grayscale hover:grayscale-0 transition-all"
                  />
                  <span className="text-[#032d28] text-[10px] font-black uppercase text-center">
                    {cert.name}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* BOTTOM CTA AREA (Centered) */}
        <div className="max-w-3xl mx-auto w-full px-6">
          <p className="text-white/80 mb-8 text-center">
            Get in touch today to learn how to devise the most cost-effective
            approach to achieving your governance, risk and compliance goals.
          </p>

          <div className="flex justify-center">
            <Link
              to="/contact"
              className="border border-white px-8 py-3 uppercase text-sm tracking-widest hover:bg-white hover:text-[#032d28] transition no-underline"
            >
              Find Out More
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
